import { c as _c } from "react/compiler-runtime";
import { CUSTOMER_FUNNEL_STAGES, MARKETING_ACTIVATION_FORMATS, MARKETING_CHANNELS, MARKETING_COLLABORATION_MODELS, MARKETING_DELIVERY_ACTIVITIES, MARKETING_DELIVERY_DISCIPLINES, MARKETING_OBJECTIVES, MARKETING_ROLES, MARKETING_TACTICS, PRODUCT_STAGES } from '@sizzlenz/shared';
import { FlaskConical, Goal, Handshake, Lightbulb, Radio, Route, Store, Target, Waypoints, Wrench } from 'lucide-react';
import Link from 'next/link';
import { buildInspirationFeedHref } from '@/lib/inspiration-filters';
import { useSheetManager } from '@/lib/ui';
import type { MarketingDetailsValue } from './MarketingDetailsForm';
type OptionDef = readonly {
  id: string;
  label: string;
}[];
interface ResolvedTag {
  id: string;
  label: string;
}
function resolveTags(keys: string[] | undefined, options: OptionDef): ResolvedTag[] {
  if (!keys?.length) return [];
  const map = new Map(options.map(o => [o.id, o.label]));
  return keys.map(id => ({
    id,
    label: map.get(id) || id
  }));
}

// Demand roles use the professional label for display consistency with the form.
const MARKETING_ROLE_LABELS = MARKETING_ROLES.map(role => ({
  id: role.id,
  label: role.professionalLabel
}));
const CHIP_CLASS = 'inline-block rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm font-medium text-gray-800 shadow-sm';
const CHIP_LINK_CLASS = 'inline-block cursor-pointer rounded-md border border-gray-200 bg-white px-2.5 py-1 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';
interface TagRowProps {
  icon: React.ReactNode;
  label: string;
  facetKey: keyof MarketingDetailsValue;
  tags: ResolvedTag[];
  /** When true, chips link through to the Inspiration Feed filtered by their value. */
  linkTags?: boolean;
  /** Called when a linked chip is clicked (e.g. to close any open sheets). */
  onNavigate?: () => void;
}
function TagRow(t0) {
  const $ = _c(21);
  const {
    icon,
    label,
    facetKey,
    tags,
    linkTags,
    onNavigate
  } = t0;
  if (!tags.length) {
    return null;
  }
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="text-primary-600 shrink-0">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-sm font-semibold text-gray-900">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== facetKey || $[8] !== linkTags || $[9] !== onNavigate || $[10] !== tags) {
    let t5;
    if ($[12] !== facetKey || $[13] !== linkTags || $[14] !== onNavigate) {
      t5 = tag => {
        const href = linkTags ? buildInspirationFeedHref(facetKey, tag.id) : undefined;
        if (href) {
          return <Link key={tag.id} href={href} className={CHIP_LINK_CLASS} onClick={onNavigate}>{tag.label}</Link>;
        }
        return <span key={tag.id} className={CHIP_CLASS}>{tag.label}</span>;
      };
      $[12] = facetKey;
      $[13] = linkTags;
      $[14] = onNavigate;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t4 = tags.map(t5);
    $[7] = facetKey;
    $[8] = linkTags;
    $[9] = onNavigate;
    $[10] = tags;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[16] !== t4) {
    t5 = <div className="flex flex-wrap gap-1.5 mt-1.5 ml-6">{t4}</div>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t3 || $[19] !== t5) {
    t6 = <div>{t3}{t5}</div>;
    $[18] = t3;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}
interface MarketingDetailsSnapshotProps {
  demand: Partial<MarketingDetailsValue>;
  className?: string;
  /**
   * When true, chips link through to the Inspiration Feed pre-filtered by their
   * value (for facets the feed supports). Defaults to false. Objectives and
   * delivery disciplines are never linkable — the feed doesn't filter on them.
   */
  linkTags?: boolean;
}

/**
 * Read-only summary of a content record's marketing attributes.
 * Mirrors the ActivationSettingsSnapshot pattern (icon + label + chips per
 * facet). Renders nothing when no attributes are set.
 */
export function MarketingDetailsSnapshot(t0) {
  const $ = _c(87);
  const {
    demand,
    className,
    linkTags: t1
  } = t0;
  const linkTags = t1 === undefined ? false : t1;
  const {
    closeAllSheets
  } = useSheetManager();
  let t2;
  if ($[0] !== closeAllSheets || $[1] !== linkTags) {
    t2 = linkTags ? () => closeAllSheets() : undefined;
    $[0] = closeAllSheets;
    $[1] = linkTags;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleNavigate = t2;
  let t3;
  if ($[3] !== demand.marketingRoles) {
    t3 = resolveTags(demand.marketingRoles, MARKETING_ROLE_LABELS);
    $[3] = demand.marketingRoles;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const roles = t3;
  let t4;
  if ($[5] !== demand.journeyStages) {
    t4 = resolveTags(demand.journeyStages, CUSTOMER_FUNNEL_STAGES);
    $[5] = demand.journeyStages;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const stages = t4;
  let t5;
  if ($[7] !== demand.productStages) {
    t5 = resolveTags(demand.productStages, PRODUCT_STAGES);
    $[7] = demand.productStages;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const productStages = t5;
  let t6;
  if ($[9] !== demand.marketingObjectives) {
    t6 = resolveTags(demand.marketingObjectives, MARKETING_OBJECTIVES);
    $[9] = demand.marketingObjectives;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const objectives = t6;
  let t7;
  if ($[11] !== demand.tactics) {
    t7 = resolveTags(demand.tactics, MARKETING_TACTICS);
    $[11] = demand.tactics;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const tactics = t7;
  let t8;
  if ($[13] !== demand.channels) {
    t8 = resolveTags(demand.channels, MARKETING_CHANNELS);
    $[13] = demand.channels;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const channels = t8;
  let t9;
  if ($[15] !== demand.deliveryDisciplines) {
    t9 = resolveTags(demand.deliveryDisciplines, MARKETING_DELIVERY_DISCIPLINES);
    $[15] = demand.deliveryDisciplines;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const disciplines = t9;
  let t10;
  if ($[17] !== demand.deliveryActivities) {
    t10 = resolveTags(demand.deliveryActivities, MARKETING_DELIVERY_ACTIVITIES);
    $[17] = demand.deliveryActivities;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const activities = t10;
  let t11;
  if ($[19] !== demand.collaborationModels) {
    t11 = resolveTags(demand.collaborationModels, MARKETING_COLLABORATION_MODELS);
    $[19] = demand.collaborationModels;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const collaborationModels = t11;
  let t12;
  if ($[21] !== demand.activationFormats) {
    t12 = resolveTags(demand.activationFormats, MARKETING_ACTIVATION_FORMATS);
    $[21] = demand.activationFormats;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const activationFormats = t12;
  const hasAnyData = roles.length > 0 || stages.length > 0 || productStages.length > 0 || objectives.length > 0 || tactics.length > 0 || channels.length > 0 || disciplines.length > 0 || activities.length > 0 || collaborationModels.length > 0 || activationFormats.length > 0;
  if (!hasAnyData) {
    return null;
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Target className="w-4 h-4" />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== handleNavigate || $[25] !== linkTags || $[26] !== roles) {
    t14 = <TagRow icon={t13} label="Marketing role" facetKey="marketingRoles" tags={roles} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[24] = handleNavigate;
    $[25] = linkTags;
    $[26] = roles;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Route className="w-4 h-4" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== handleNavigate || $[30] !== linkTags || $[31] !== stages) {
    t16 = <TagRow icon={t15} label="Journey stage" facetKey="journeyStages" tags={stages} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[29] = handleNavigate;
    $[30] = linkTags;
    $[31] = stages;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FlaskConical className="w-4 h-4" />;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== handleNavigate || $[35] !== linkTags || $[36] !== productStages) {
    t18 = <TagRow icon={t17} label="Product stage" facetKey="productStages" tags={productStages} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[34] = handleNavigate;
    $[35] = linkTags;
    $[36] = productStages;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Goal className="w-4 h-4" />;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== handleNavigate || $[40] !== linkTags || $[41] !== objectives) {
    t20 = <TagRow icon={t19} label="Objective" facetKey="marketingObjectives" tags={objectives} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[39] = handleNavigate;
    $[40] = linkTags;
    $[41] = objectives;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Lightbulb className="w-4 h-4" />;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== handleNavigate || $[45] !== linkTags || $[46] !== tactics) {
    t22 = <TagRow icon={t21} label="Tactic" facetKey="tactics" tags={tactics} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[44] = handleNavigate;
    $[45] = linkTags;
    $[46] = tactics;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Radio className="w-4 h-4" />;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== channels || $[50] !== handleNavigate || $[51] !== linkTags) {
    t24 = <TagRow icon={t23} label="Channel" facetKey="channels" tags={channels} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[49] = channels;
    $[50] = handleNavigate;
    $[51] = linkTags;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Waypoints className="w-4 h-4" />;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== disciplines || $[55] !== handleNavigate || $[56] !== linkTags) {
    t26 = <TagRow icon={t25} label="Delivery discipline" facetKey="deliveryDisciplines" tags={disciplines} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[54] = disciplines;
    $[55] = handleNavigate;
    $[56] = linkTags;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Wrench className="w-4 h-4" />;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== activities || $[60] !== handleNavigate || $[61] !== linkTags) {
    t28 = <TagRow icon={t27} label="Execution activity" facetKey="deliveryActivities" tags={activities} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[59] = activities;
    $[60] = handleNavigate;
    $[61] = linkTags;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Handshake className="w-4 h-4" />;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== collaborationModels || $[65] !== handleNavigate || $[66] !== linkTags) {
    t30 = <TagRow icon={t29} label="Collaboration model" facetKey="collaborationModels" tags={collaborationModels} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[64] = collaborationModels;
    $[65] = handleNavigate;
    $[66] = linkTags;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Store className="w-4 h-4" />;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== activationFormats || $[70] !== handleNavigate || $[71] !== linkTags) {
    t32 = <TagRow icon={t31} label="Activation format" facetKey="activationFormats" tags={activationFormats} linkTags={linkTags} onNavigate={handleNavigate} />;
    $[69] = activationFormats;
    $[70] = handleNavigate;
    $[71] = linkTags;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t14 || $[74] !== t16 || $[75] !== t18 || $[76] !== t20 || $[77] !== t22 || $[78] !== t24 || $[79] !== t26 || $[80] !== t28 || $[81] !== t30 || $[82] !== t32) {
    t33 = <div className="space-y-4">{t14}{t16}{t18}{t20}{t22}{t24}{t26}{t28}{t30}{t32}</div>;
    $[73] = t14;
    $[74] = t16;
    $[75] = t18;
    $[76] = t20;
    $[77] = t22;
    $[78] = t24;
    $[79] = t26;
    $[80] = t28;
    $[81] = t30;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== className || $[85] !== t33) {
    t34 = <div className={className}>{t33}</div>;
    $[84] = className;
    $[85] = t33;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  return t34;
}